import { useCallback, useEffect, useRef, useState } from 'react'
import { api, COLLECTIONS, type Collection, type CollectionKey, type RecordRow } from '../api/client'
import { useAuth } from '../store/auth'
import { useSettings } from '../store/settings'
import { estimateRemainingMs, HEURISTIC_TOTAL, rateRecordsPerSec, type ProgressSample } from '../utils/eta'
import type { ProgressInfo } from '../components/ProgressOverlay'

export type LoaderState = {
  rows: RecordRow[]
  loading: boolean
  error: string | null
  progress: ProgressInfo | null
  newestRkey: string | null
  count: number
}

export function useFeedLoader(handle: string | null, key: CollectionKey) {
  const service = useSettings((s) => s.service)
  const pageSize = useSettings((s) => s.pageSize)
  const accessJwt = useAuth((s) => s.session?.accessJwt)
  const collection: Collection = COLLECTIONS[key]
  const [state, setState] = useState<LoaderState>({
    rows: [],
    loading: false,
    error: null,
    progress: null,
    newestRkey: null,
    count: 0,
  })
  const abortRef = useRef<{ cancelled: boolean; controller: AbortController } | null>(null)

  const refreshFromCache = useCallback(async () => {
    if (!handle) return
    try {
      const data = await api.cache(handle, collection)
      setState((s) => ({ ...s, rows: data.records, newestRkey: data.newestRkey, count: data.count }))
    } catch (err) {
      setState((s) => ({ ...s, error: err instanceof Error ? err.message : String(err) }))
    }
  }, [handle, collection])

  useEffect(() => {
    setState({
      rows: [],
      loading: false,
      error: null,
      progress: null,
      newestRkey: null,
      count: 0,
    })
    refreshFromCache()
  }, [refreshFromCache])

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.cancelled = true
      abortRef.current.controller.abort()
    }
    setState((s) => ({ ...s, progress: null, loading: false }))
  }, [])

  // Abort any in-flight loadAll on unmount so leaving the profile mid-pull
  // doesn't keep walking the PDS for a profile the user has navigated away
  // from. Also fires when the loader's handle/collection changes.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.cancelled = true
        abortRef.current.controller.abort()
      }
    }
  }, [handle, collection])

  const fetchSinglePage = useCallback(async () => {
    if (!handle) return
    setState((s) => ({ ...s, loading: true, error: null }))
    const ab = { cancelled: false, controller: new AbortController() }
    abortRef.current = ab
    try {
      const cache = await api.cache(handle, collection)
      const stop = cache.newestRkey ?? undefined
      await api.page({
        handle,
        collection,
        limit: pageSize,
        stopAtRkey: stop,
        service,
        accessJwt,
        signal: ab.controller.signal,
      })
      if (ab.cancelled) return
      await refreshFromCache()
      setState((s) => ({ ...s, loading: false, progress: null }))
    } catch (err) {
      if (ab.cancelled) return
      if (err instanceof DOMException && err.name === 'AbortError') return
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }))
    }
  }, [handle, collection, pageSize, service, accessJwt, refreshFromCache])

  const loadAll = useCallback(async () => {
    if (!handle) return

    const guessTotal = HEURISTIC_TOTAL[collection] ?? 5000
    const ab = { cancelled: false, controller: new AbortController() }
    abortRef.current = ab
    const startedAt = performance.now()
    const samples: ProgressSample[] = []
    let fetched = 0
    let pages = 0
    let cursor: string | undefined = undefined

    setState((s) => ({
      ...s,
      loading: true,
      error: null,
      progress: {
        fetched: 0,
        pages: 0,
        rate: 0,
        elapsedMs: 0,
        estimatedRemainingMs: null,
        guessTotal,
        hitCache: false,
        done: false,
      },
    }))

    let lastError: string | null = null
    let consecutiveFailures = 0
    while (!ab.cancelled) {
      const t0 = performance.now()
      let page
      let attempt = 0
      while (true) {
        try {
          page = await api.page({
            handle,
            collection,
            cursor,
            limit: pages === 0 ? pageSize : 100,
            service,
            accessJwt,
            signal: ab.controller.signal,
          })
          break
        } catch (err) {
          attempt += 1
          const msg = err instanceof Error ? err.message : String(err)
          lastError = msg
          if (
            ab.cancelled ||
            (err instanceof DOMException && err.name === 'AbortError')
          ) {
            page = null
            break
          }
          if (attempt >= 4) {
            consecutiveFailures += 1
            page = null
            break
          }
          // 429 or 5xx — back off; otherwise short retry
          const is429 = /\b429\b/.test(msg)
          const wait = is429 ? 8000 * attempt : 750 * 2 ** attempt
          await new Promise((res) => setTimeout(res, Math.min(wait, 30_000)))
        }
      }
      if (!page) {
        if (consecutiveFailures >= 3) {
          // give up: bsky probably hard-rate-limiting us
          setState((s) => ({
            ...s,
            error: `Stopped after repeated failures: ${lastError ?? 'unknown'}`,
          }))
          break
        }
        // skip this page (cursor unchanged means we'd loop forever) — bail
        break
      }
      consecutiveFailures = 0
      samples.push({ records: page.records.length, ms: performance.now() - t0 })
      fetched += page.records.length
      pages += 1
      cursor = page.cursor ?? undefined

      setState((s) => ({
        ...s,
        progress: {
          fetched,
          pages,
          rate: rateRecordsPerSec(samples.slice(-5)),
          elapsedMs: performance.now() - startedAt,
          estimatedRemainingMs: estimateRemainingMs(samples.slice(-5), fetched, guessTotal),
          guessTotal,
          hitCache: false,
          done: false,
        },
      }))

      // No mid-load refreshFromCache — that re-renders the entire RecordList
      // every few pages and at million scale freezes the tab. We only refresh
      // when the load completes (or breaks).
      if (!cursor) break
    }

    await refreshFromCache()
    setState((s) => ({
      ...s,
      loading: false,
      progress: s.progress ? { ...s.progress, done: true, estimatedRemainingMs: 0 } : null,
    }))
    setTimeout(() => setState((s) => ({ ...s, progress: null })), 1500)
  }, [handle, collection, pageSize, service, accessJwt, refreshFromCache])

  const clearCache = useCallback(async () => {
    if (!handle) return
    await api.clearCache(handle, collection)
    await refreshFromCache()
  }, [handle, collection, refreshFromCache])

  return {
    ...state,
    fetchSinglePage,
    loadAll,
    cancel,
    clearCache,
    refreshFromCache,
  }
}
